/**
 * Dettaglio di UNA regola del motore verifiche fiscali (Task V14): i singoli
 * rilievi aperti (o nello stato scelto) su quella regola, un documento per
 * riga, con le azioni di decisione.
 *
 * Dopo un'azione la riga si aggiorna IN LOCALE (rimossa se non appartiene
 * piu' alla vista corrente, sostituita altrimenti) invece di rifare la GET:
 * l'elenco e' ordinato per updated_at DESC (verifiche.py:386), una colonna
 * che l'azione stessa modifica - un refetch subito dopo spostrebbe le righe
 * sotto le mani mentre si continua a decidere sulle successive.
 *
 * Le azioni cambiano con lo stato filtrato (mai le stesse quattro ovunque):
 * - 'aperto': Conferma, Archivia (motivo obbligatorio), Sospendi/Riattiva.
 * - 'confermato': Archivia, Sospendi/Riattiva (non "Conferma": lo e' gia').
 * - 'archiviato' / 'risolto_automatico': sola lettura, si vede l'esito.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  toast,
} from '@floraltech/ui';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUrlBoolState, useUrlNumberState, useUrlState } from '@lib/useUrlState';
import { useAuth } from '@shell/AuthContext';
import { useFormatters } from '@lib/formatters';
import GravitaBadge from '../components/GravitaBadge';
import {
  etichettaNaturaDanno,
  fineMeseLocale,
  formattaDataSolo,
  oggiLocale,
  piuGiorniLocale,
  STATI_RILIEVO,
} from '../lib/verifiche';

interface DocumentoRilievo {
  id: string;
  year: number;
  numero: string | null;
  data: string;
  controparte: string | null;
}

interface Rilievo {
  id: string;
  codice: string;
  norma: string;
  titolo: string;
  dettaglio: string;
  gravita: string;
  stato: string;
  natura_danno: string;
  sospeso_fino: string | null;
  deciso_da: string | null;
  deciso_at: string | null;
  motivazione: string | null;
  evidenza: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  documento: DocumentoRilievo;
}

interface RilievoGruppo {
  codice: string;
  natura_danno: string;
  norma: string;
  titolo: string;
  conteggio: number;
}

const DIMENSIONI_PAGINA = [25, 50, 100, 200]; // MAX_PAGE_SIZE backend = 200

const auth = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Estrae il messaggio d'errore di un 4xx del router (`detail` stringa o array FastAPI). */
async function messaggioErrore(res: Response): Promise<string> {
  const body = await res.json().catch(() => null);
  if (typeof body?.detail === 'string') return body.detail;
  return `Errore ${res.status}`;
}

const VerificheRegolaDetail: React.FC = () => {
  const { codice } = useParams<{ codice: string }>();
  const navigate = useNavigate();
  const { hasPermesso } = useAuth();
  const fmt = useFormatters();
  const puoModificare = hasPermesso('documentale.modifica');

  const [stato, setStato] = useUrlState('stato', 'aperto');
  const [sospesi, setSospesi] = useUrlBoolState('sospesi', false);
  const [pagina, setPagina] = useUrlNumberState('p', 1);
  const [perPagina, setPerPagina] = useUrlNumberState('n', 50);

  const [testata, setTestata] = useState<RilievoGruppo | null>(null);
  const [totale, setTotale] = useState<number | null>(null);
  const [rilievi, setRilievi] = useState<Rilievo[]>([]);
  const [loading, setLoading] = useState(true);
  const [ricaricando, setRicaricando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [espansi, setEspansi] = useState<Set<string>>(new Set());

  const statoEtichetta = STATI_RILIEVO.find((s) => s.codice === stato)?.etichetta ?? stato;
  const soloLettura = stato === 'archiviato' || stato === 'risolto_automatico';

  // Guardia anti-sorpasso: due cambi rapidi di tab/switch possono far
  // atterrare per ultima la risposta di una richiesta precedente (dali,
  // review V14). Un contatore per funzione, incrementato a ogni chiamata:
  // solo l'ultima generazione avviata puo' scrivere lo stato.
  const generazioneTestataRef = useRef(0);
  const generazioneRilieviRef = useRef(0);

  /** Il totale NON viene dalla lista paginata (che non lo porta,
   *  verifiche.py:348-391): si legge dal `conteggio` della stessa riga nella
   *  lista raggruppata, con GLI STESSI filtri stato/sospesi - stesso
   *  principio di FattureList.tsx (riepilogo separato dall'elenco). */
  const caricaTestata = useCallback(async () => {
    if (!codice) return;
    generazioneTestataRef.current += 1;
    const generazione = generazioneTestataRef.current;
    try {
      const params = new URLSearchParams({ stato, sospesi: sospesi ? 'true' : 'false' });
      const res = await fetch(`/api/v1/verifiche?${params.toString()}`, { headers: auth() });
      if (generazioneTestataRef.current !== generazione) return;
      if (!res.ok) return;
      const gruppi = (await res.json()) as RilievoGruppo[];
      if (generazioneTestataRef.current !== generazione) return;
      const mio = gruppi.find((g) => g.codice === codice) ?? null;
      setTestata(mio);
      setTotale(mio?.conteggio ?? 0);
    } catch {
      /* La testata (titolo/norma) e' un aiuto alla lettura, non blocca
         l'elenco: se non arriva restano solo il codice e i dati di riga. */
    }
  }, [codice, stato, sospesi]);

  const caricaRilievi = useCallback(async () => {
    if (!codice) return;
    generazioneRilieviRef.current += 1;
    const generazione = generazioneRilieviRef.current;
    setRicaricando(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        stato,
        sospesi: sospesi ? 'true' : 'false',
        limit: String(perPagina),
        offset: String(Math.max(0, (pagina - 1) * perPagina)),
      });
      const res = await fetch(`/api/v1/verifiche/${encodeURIComponent(codice)}?${params.toString()}`, {
        headers: auth(),
      });
      if (generazioneRilieviRef.current !== generazione) return;
      if (!res.ok) throw new Error(await messaggioErrore(res));
      setRilievi((await res.json()) as Rilievo[]);
    } catch (err) {
      if (generazioneRilieviRef.current === generazione) {
        setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      }
    } finally {
      if (generazioneRilieviRef.current === generazione) {
        setLoading(false);
        setRicaricando(false);
      }
    }
  }, [codice, stato, sospesi, pagina, perPagina]);

  useEffect(() => {
    void caricaTestata();
  }, [caricaTestata]);
  useEffect(() => {
    void caricaRilievi();
  }, [caricaRilievi]);

  // Cambiando stato/sospesi si torna a pagina 1: restare su una pagina che
  // nel nuovo insieme potrebbe non esistere piu' mostrerebbe una lista vuota
  // che sembra "nessun rilievo" invece di "pagina sbagliata". MA non al
  // primo render (dali, review V14): senza il guard, tornare da una fattura
  // (o un F5) su "pagina 2" faceva scattare questo stesso effect al mount e
  // azzerava `p` nella URL - esattamente il difetto che la URL doveva
  // chiudere (back-navigation che non ripristina lo stato della lista).
  const primoRenderStatoSospesi = useRef(true);
  useEffect(() => {
    if (primoRenderStatoSospesi.current) {
      primoRenderStatoSospesi.current = false;
      return;
    }
    setPagina(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stato, sospesi]);

  /** Applica l'esito di un'azione: sostituisce la riga se resta nella vista
   *  corrente, la rimuove se ne e' uscita (stato cambiato, o sospensione
   *  futura mentre "Includi sospesi" e' spento), la RIMETTE se ci rientra e
   *  non c'era piu' (caso "Annulla" nel toast di Sospendi -> riattiva: la
   *  riga era stata tolta da Sospendi, senza questo ramo il map su un
   *  array che non la contiene piu' sarebbe un no-op silenzioso - bug
   *  trovato da dali in review). Reinserita in testa: l'elenco e' ordinato
   *  per updated_at DESC (verifiche.py:386), e l'azione appena fatta e'
   *  l'ultima in ordine di tempo. */
  const applicaEsito = useCallback(
    (aggiornato: Rilievo) => {
      const oggi = oggiLocale();
      const esclusoPerSospensione =
        !sospesi && aggiornato.sospeso_fino !== null && aggiornato.sospeso_fino > oggi;
      const restaNellaVista = aggiornato.stato === stato && !esclusoPerSospensione;

      setRilievi((prev) => {
        const eraPresente = prev.some((r) => r.id === aggiornato.id);
        if (!restaNellaVista) return prev.filter((r) => r.id !== aggiornato.id);
        if (!eraPresente) return [aggiornato, ...prev];
        return prev.map((r) => (r.id === aggiornato.id ? aggiornato : r));
      });
      setTotale((prev) => {
        if (prev === null) return prev;
        const eraPresente = rilievi.some((r) => r.id === aggiornato.id);
        if (!restaNellaVista && eraPresente) return Math.max(0, prev - 1);
        if (restaNellaVista && !eraPresente) return prev + 1;
        return prev;
      });
    },
    [stato, sospesi, rilievi],
  );

  // Se la pagina corrente e' vuota ma `totale` dice che c'e' ancora
  // qualcosa (un'azione ha svuotato la pagina, o `totale` e' semplicemente
  // scaduto), si tenta UNA SOLA volta per coppia (stato+sospesi+pagina):
  // pagina>1 torna indietro di una, pagina 1 ricarica se stessa. Se anche il
  // secondo tentativo (stessa coppia) resta vuoto, il problema non e' la
  // pagina: e' `totale` a essere sbagliato (discordanza fra il conteggio
  // della testata e i rilievi realmente presenti). A quel punto si
  // ricarica SOLO la testata, si mostra un avviso e ci si FERMA - niente
  // cicli di fetch (dali, review V14 sul codice costruito).
  const discordanzaRef = useRef<{ chiave: string; tentativi: number }>({ chiave: '', tentativi: 0 });
  const [discordanzaAvviso, setDiscordanzaAvviso] = useState(false);

  useEffect(() => {
    if (loading || ricaricando) return;
    const chiave = `${stato}|${sospesi}|${pagina}`;

    if (rilievi.length > 0) {
      // La pagina e' valida: azzera il contatore di questa chiave (se un
      // giorno si ripresenta vuota, riparte da un tentativo pulito) e
      // spegne un eventuale avviso residuo.
      if (discordanzaRef.current.chiave === chiave) discordanzaRef.current = { chiave: '', tentativi: 0 };
      if (discordanzaAvviso) setDiscordanzaAvviso(false);
      return;
    }
    if ((totale ?? 0) <= 0) {
      // Davvero vuoto (totale 0): non e' una discordanza, e' lo stato reale.
      if (discordanzaAvviso) setDiscordanzaAvviso(false);
      return;
    }

    if (discordanzaRef.current.chiave !== chiave) {
      discordanzaRef.current = { chiave, tentativi: 1 };
      if (pagina > 1) setPagina(Math.max(1, pagina - 1));
      else void caricaRilievi();
      return;
    }
    if (discordanzaRef.current.tentativi === 1) {
      discordanzaRef.current.tentativi = 2;
      setDiscordanzaAvviso(true);
      void caricaTestata();
    }
    // tentativi >= 2: fermo, l'avviso resta finche' l'utente non agisce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rilievi.length, loading, ricaricando, pagina, totale, stato, sospesi]);

  const conferma = async (id: string) => {
    try {
      const res = await fetch(`/api/v1/verifiche/${id}/conferma`, { method: 'POST', headers: auth() });
      if (!res.ok) {
        toast.error(await messaggioErrore(res));
        return;
      }
      const aggiornato = (await res.json()) as Rilievo;
      applicaEsito(aggiornato);
      toast.success('Rilievo confermato.');
    } catch {
      toast.error('Errore di rete: verifica la connessione e riprova.');
    }
  };

  const riattiva = async (id: string) => {
    try {
      const res = await fetch(`/api/v1/verifiche/${id}/riattiva`, { method: 'POST', headers: auth() });
      if (!res.ok) {
        toast.error(await messaggioErrore(res));
        return;
      }
      const aggiornato = (await res.json()) as Rilievo;
      applicaEsito(aggiornato);
      toast.success('Sospensione annullata: il rilievo torna in coda.');
    } catch {
      toast.error('Errore di rete: verifica la connessione e riprova.');
    }
  };

  /* --- Dialog Archivia (motivazione obbligatoria) --- */
  const [archiviaId, setArchiviaId] = useState<string | null>(null);
  const [motivazione, setMotivazione] = useState('');
  const [archiviaSalvataggio, setArchiviaSalvataggio] = useState(false);
  const [archiviaErrore, setArchiviaErrore] = useState<string | null>(null);

  const apriArchivia = (id: string) => {
    setArchiviaId(id);
    setMotivazione('');
    setArchiviaErrore(null);
  };

  const confermaArchivia = async () => {
    if (!archiviaId) return;
    if (!motivazione.trim()) {
      setArchiviaErrore("La motivazione e' obbligatoria: resta agli atti.");
      return;
    }
    setArchiviaSalvataggio(true);
    setArchiviaErrore(null);
    try {
      const res = await fetch(`/api/v1/verifiche/${archiviaId}/archivia`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivazione: motivazione.trim() }),
      });
      if (!res.ok) {
        setArchiviaErrore(await messaggioErrore(res));
        return;
      }
      const aggiornato = (await res.json()) as Rilievo;
      applicaEsito(aggiornato);
      toast.success('Rilievo archiviato.');
      setArchiviaId(null);
    } catch {
      setArchiviaErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setArchiviaSalvataggio(false);
    }
  };

  /* --- Dialog Sospendi (data futura obbligatoria) --- */
  const [sospendiId, setSospendiId] = useState<string | null>(null);
  const [dataSospensione, setDataSospensione] = useState(piuGiorniLocale(7));
  const [sospendiSalvataggio, setSospendiSalvataggio] = useState(false);
  const [sospendiErrore, setSospendiErrore] = useState<string | null>(null);

  const apriSospendi = (id: string) => {
    setSospendiId(id);
    setDataSospensione(piuGiorniLocale(7));
    setSospendiErrore(null);
  };

  const confermaSospendi = async () => {
    if (!sospendiId) return;
    if (!dataSospensione || dataSospensione <= oggiLocale()) {
      setSospendiErrore('La data deve essere futura rispetto a oggi.');
      return;
    }
    setSospendiSalvataggio(true);
    setSospendiErrore(null);
    try {
      const res = await fetch(`/api/v1/verifiche/${sospendiId}/sospendi`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sospeso_fino: dataSospensione }),
      });
      if (!res.ok) {
        setSospendiErrore(await messaggioErrore(res));
        return;
      }
      const aggiornato = (await res.json()) as Rilievo;
      applicaEsito(aggiornato);
      const idSospeso = sospendiId;
      const finoA = formattaDataSolo(dataSospensione);
      setSospendiId(null);
      toast.success(`Sospeso fino al ${finoA}.`, {
        action: { label: 'Annulla', onClick: () => void riattiva(idSospeso) },
      });
    } catch {
      setSospendiErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setSospendiSalvataggio(false);
    }
  };

  const toggleEspanso = (id: string) => {
    setEspansi((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ultimaPagina = totale === null ? pagina : Math.max(1, Math.ceil(totale / perPagina));

  const oggi = useMemo(() => oggiLocale(), []);

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          &larr; Indietro
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
            {testata?.titolo ?? codice}
          </h1>
          <p className="text-sm text-muted-foreground">{testata?.norma}</p>
        </div>
        {testata && <Badge variant="outline">{etichettaNaturaDanno(testata.natura_danno)}</Badge>}
        <Badge variant="secondary">{statoEtichetta}</Badge>
        <span className="font-mono text-xs text-muted-foreground">{codice}</span>
        {!soloLettura && (
          <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <input
              id="verifiche-regola-includi-sospesi"
              type="checkbox"
              checked={sospesi}
              onChange={(e) => setSospesi(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <label htmlFor="verifiche-regola-includi-sospesi">Includi sospesi</label>
            <Tooltip label="Un rilievo sospeso resta nel suo stato: il motore non lo tocca finche' non passa la data di sospensione. Di default e' escluso, attiva questo switch per vederlo comunque, con l'indicazione di fino a quando.">
              <button
                type="button"
                aria-label="Cosa significa sospeso"
                className="text-muted-foreground hover:text-foreground"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <span className="text-destructive">{error}</span>
          <Button variant="outline" size="sm" onClick={() => void caricaRilievi()}>
            Riprova
          </Button>
        </div>
      )}

      {discordanzaAvviso && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-muted/50 p-3 text-sm"
        >
          <span>
            Il conteggio non corrisponde ai rilievi trovati (probabile azione recente, anche da
            un'altra sessione): il totale e' stato aggiornato.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              discordanzaRef.current = { chiave: '', tentativi: 0 };
              setDiscordanzaAvviso(false);
              void caricaTestata();
              void caricaRilievi();
            }}
          >
            Aggiorna
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table aria-busy={ricaricando} className={ricaricando ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <TableHeader>
              <TableRow>
                <TableHead>Documento</TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-1">
                    Gravita'
                    <Tooltip label="'Blocco' impedisce l'invio/la corretta tenuta fiscale del documento; 'Attenzione' segnala un problema che non blocca ma va valutato.">
                      <button
                        type="button"
                        aria-label="Cosa significa gravita'"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead>Rilievo</TableHead>
                {soloLettura ? <TableHead>Esito</TableHead> : null}
                {!soloLettura && puoModificare ? <TableHead className="text-right">Azioni</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && !ricaricando && rilievi.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={soloLettura || puoModificare ? 4 : 3}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    Nessun rilievo {statoEtichetta.toLowerCase()} per questa regola.
                  </TableCell>
                </TableRow>
              )}
              {rilievi.map((r) => {
                const espanso = espansi.has(r.id);
                const sospesoAncora = r.sospeso_fino !== null && r.sospeso_fino > oggi;
                const sospesoScaduto = r.sospeso_fino !== null && r.sospeso_fino <= oggi;
                return (
                  <React.Fragment key={r.id}>
                    <TableRow>
                      <TableCell className="align-top">
                        <Link
                          to={`/documentale/fatture/${r.documento.id}?year=${r.documento.year}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {r.documento.numero ?? r.documento.id.slice(0, 8)}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {formattaDataSolo(r.documento.data)}
                          {r.documento.controparte ? ` - ${r.documento.controparte}` : ''}
                        </div>
                        {sospesoAncora && (
                          <Badge variant="warning" className="mt-1">
                            In attesa fino al {formattaDataSolo(r.sospeso_fino as string)}
                          </Badge>
                        )}
                        {sospesoScaduto && (
                          <Badge variant="outline" className="mt-1">
                            Sospensione scaduta il {formattaDataSolo(r.sospeso_fino as string)} - e' tornato in coda
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <GravitaBadge gravita={r.gravita} />
                      </TableCell>
                      <TableCell className="align-top">
                        <p className={espanso ? '' : 'max-w-md truncate'} title={!espanso ? r.dettaglio : undefined}>
                          {r.dettaglio}
                        </p>
                        {(r.dettaglio.length > 90 || (r.evidenza && Object.keys(r.evidenza).length > 0)) && (
                          <button
                            type="button"
                            className="mt-1 text-xs text-primary hover:underline"
                            onClick={() => toggleEspanso(r.id)}
                          >
                            {espanso ? 'Mostra meno' : 'Mostra tutto / evidenza'}
                          </button>
                        )}
                        {espanso && r.evidenza && Object.keys(r.evidenza).length > 0 && (
                          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 rounded border border-border bg-muted/40 p-2 text-xs">
                            {Object.entries(r.evidenza).map(([chiave, valore]) => (
                              <React.Fragment key={chiave}>
                                <dt className="font-medium text-muted-foreground">{chiave}</dt>
                                <dd className="font-mono">
                                  {typeof valore === 'object' ? JSON.stringify(valore) : String(valore)}
                                </dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        )}
                      </TableCell>
                      {soloLettura && (
                        <TableCell className="align-top text-sm">
                          {r.motivazione && <p>{r.motivazione}</p>}
                          {r.deciso_at && (
                            <p className="text-xs text-muted-foreground">
                              {fmt.dateTime(r.deciso_at)}
                            </p>
                          )}
                        </TableCell>
                      )}
                      {!soloLettura && puoModificare && (
                        <TableCell className="align-top">
                          <div className="flex flex-wrap items-center justify-end gap-4">
                            {stato === 'aperto' && (
                              <button
                                type="button"
                                className="min-h-6 px-1 text-xs text-primary hover:underline"
                                onClick={() => void conferma(r.id)}
                              >
                                Conferma
                              </button>
                            )}
                            {/* Distinta dalle altre (Conferma/Sospendi/Riattiva sono
                                benigne, reversibili): Archivia e' l'unica senza ritorno
                                automatico, stesso trattamento di "Elimina" in
                                FatturaDetail.tsx (text-destructive vs text-primary). */}
                            <button
                              type="button"
                              className="min-h-6 px-1 text-xs text-destructive hover:underline"
                              onClick={() => apriArchivia(r.id)}
                            >
                              Archivia
                            </button>
                            {r.sospeso_fino === null ? (
                              <button
                                type="button"
                                className="min-h-6 px-1 text-xs text-primary hover:underline"
                                onClick={() => apriSospendi(r.id)}
                              >
                                Sospendi
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="min-h-6 px-1 text-xs text-primary hover:underline"
                                onClick={() => void riattiva(r.id)}
                              >
                                Riattiva
                              </button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginazione - nascosta quando c'e' una sola pagina (dali, review
          V14): "Pagina 1 di 1" con i bottoni Precedente/Successiva sempre
          disabilitati non informa, occupa solo spazio. */}
      {!error && (totale === null || totale > 0) && (totale === null || ultimaPagina > 1) && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Righe per pagina</span>
            <Select
              value={String(perPagina)}
              onValueChange={(v) => {
                setPerPagina(Number(v));
                setPagina(1);
              }}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSIONI_PAGINA.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={ricaricando || pagina <= 1}
              onClick={() => setPagina(Math.max(1, pagina - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Precedente
            </Button>
            <span className="px-1 text-muted-foreground">
              Pagina {pagina}
              {totale !== null && ` di ${ultimaPagina}`}
              {totale !== null && ` (${totale} rilievi)`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={ricaricando || (totale !== null ? pagina >= ultimaPagina : rilievi.length < perPagina)}
              onClick={() => setPagina(pagina + 1)}
            >
              Successiva
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog Archivia - motivazione obbligatoria (ck_verifiche_archiviato_motivato) */}
      <Dialog open={archiviaId !== null} onOpenChange={(open) => !open && setArchiviaId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archivia rilievo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Guardato e va bene cosi': indica il motivo, resta agli atti.
            </p>
            {/* Irreversibilita' dichiarata (dali, review V14): a differenza di
                Sospendi (che ha Riattiva) non esiste un endpoint che riporti
                un rilievo archiviato ad aperto. */}
            <p className="text-sm text-destructive">
              Non tornera' in coda: il motore lo ripropone solo se i valori del documento
              cambiano.
            </p>
            <div>
              <Label htmlFor="verifiche-motivazione">Motivazione</Label>
              <Textarea
                id="verifiche-motivazione"
                value={motivazione}
                onChange={(e) => setMotivazione(e.target.value)}
                className="mt-1"
                rows={3}
                placeholder="Perche' questo rilievo si archivia..."
                autoFocus
              />
            </div>
            {archiviaErrore && (
              <p role="alert" className="text-sm text-destructive">
                {archiviaErrore}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiviaId(null)} disabled={archiviaSalvataggio}>
              Annulla
            </Button>
            {/* niente !motivazione.trim() nel disabled (dali, review V14): con
                quella clausola il click restava impossibile a campo vuoto e il
                messaggio di errore sotto (role="alert") non poteva mai comparire -
                un bottone grigio muto che non spiega perche'. */}
            <Button variant="destructive" onClick={() => void confermaArchivia()} disabled={archiviaSalvataggio}>
              {archiviaSalvataggio ? 'Salvataggio...' : 'Archivia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Sospendi - data futura obbligatoria */}
      <Dialog open={sospendiId !== null} onOpenChange={(open) => !open && setSospendiId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sospendi rilievo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Il rilievo resta nel suo stato: sparisce dalla coda fino alla data scelta, poi torna
              visibile da solo.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setDataSospensione(piuGiorniLocale(7))}>
                +7 giorni
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDataSospensione(piuGiorniLocale(30))}>
                +30 giorni
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDataSospensione(fineMeseLocale())}>
                Fine mese
              </Button>
            </div>
            <div>
              <Label htmlFor="verifiche-sospeso-fino">Sospeso fino al</Label>
              <Input
                id="verifiche-sospeso-fino"
                type="date"
                value={dataSospensione}
                min={piuGiorniLocale(1)}
                onChange={(e) => setDataSospensione(e.target.value)}
                className="mt-1"
              />
            </div>
            {sospendiErrore && (
              <p role="alert" className="text-sm text-destructive">
                {sospendiErrore}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSospendiId(null)} disabled={sospendiSalvataggio}>
              Annulla
            </Button>
            <Button onClick={() => void confermaSospendi()} disabled={sospendiSalvataggio}>
              {sospendiSalvataggio ? 'Salvataggio...' : 'Sospendi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificheRegolaDetail;
